<template>
  <div class="ga-admin-page">
    <AdminHeader
      :title="$t('ga_admin_overview_title')"
      :subtitle="$t('ga_admin_overview_subtitle')"
      compact
    />

    <div class="page-body">
      <div v-if="loading" class="loading-card">
        <div class="loading-spinner"></div>
        <div class="loading-text">{{ $t('ga_admin_loading_overview') }}</div>
      </div>
      <div v-else-if="errorMessage" class="state-card warning">{{ errorMessage }}</div>
      <template v-else>

        <section class="section-card">
          <div class="section-head">
            <div>
              <div class="section-title">{{ $t('ga_admin_section_overall') }}</div>
              <div class="section-desc">{{ $t('ga_admin_section_overall_desc') }}</div>
            </div>
          </div>
          <div class="stats-grid">
            <div v-for="item in overviewStats" :key="item.title" class="stat-card">
              <div class="stat-title">{{ item.title }}</div>
              <div class="stat-value">{{ item.value }}</div>
              <div :class="['stat-change', item.tone]">{{ item.change }}</div>
              <div class="stat-desc">{{ item.desc }}</div>
            </div>
          </div>
        </section>

        <section class="section-card">
          <div class="section-head">
            <div>
              <div class="section-title">{{ $t('ga_admin_section_ai_daily') }}</div>
              <div class="section-desc">{{ $t('ga_admin_section_ai_daily_desc') }}</div>
            </div>
            <button class="ghost-btn ai-action-btn" @click="openAi($t('ga_admin_ai_prompt_daily'))">
              <font-awesome-icon :icon="['far', 'lightbulb']" class="ai-btn-icon" />
              <span>{{ $t('ga_admin_further_analysis') }}</span>
            </button>
          </div>
          <div class="summary-list">
            <div v-for="item in aiHighlights" :key="item" class="summary-item">
              <span class="summary-dot"></span>
              <span>{{ item }}</span>
            </div>
          </div>
        </section>

        <section class="section-card">
          <div class="section-head">
            <div>
              <div class="section-title">{{ $t('ga_admin_section_feedback_overview') }}</div>
              <div class="section-desc">{{ $t('ga_admin_section_feedback_overview_desc') }}</div>
            </div>
          </div>
          <div class="board-grid">
            <div v-for="board in mindsetBoards" :key="board.title" class="board-card">
              <div class="board-title">{{ board.title }}</div>
              <div class="board-subtitle">{{ board.subtitle }}</div>
              <div class="board-summary">{{ board.summary }}</div>
              <div class="metric-row">
                <div v-for="metric in board.metrics" :key="metric.label" class="metric-chip">
                  <span class="metric-label">{{ metric.label }}</span>
                  <span class="metric-value">{{ metric.value }}</span>
                </div>
              </div>
              <div class="tag-row">
                <span v-for="tag in board.tags" :key="tag" class="soft-tag">{{ tag }}</span>
                <span v-if="!board.tags.length" class="soft-tag empty">{{ $t('ga_admin_empty_feedback_tags') }}</span>
              </div>
            </div>
          </div>
          <div class="rule-note">
            <div v-for="rule in positiveFeedbackRules" :key="rule" class="rule-row">{{ rule }}</div>
          </div>
        </section>
      </template>

    </div>
  </div>
</template>

<script>
import AdminHeader from './components/AdminHeader.vue';
import {loadGaAdminAnalytics} from './analytics';

export default {
  name: 'GAAdminOverview',
  components: {
    AdminHeader,
  },
  data() {
    return {
      loading: false,
      errorMessage: '',
      analytics: {
        overviewStats: [],
        aiHighlights: [],
        mindsetBoards: [],
        positiveFeedbackRules: [],
      },
    };
  },
  computed: {
    overviewStats() {
      return this.analytics.overviewStats;
    },
    aiHighlights() {
      return this.analytics.aiHighlights;
    },
    mindsetBoards() {
      return this.analytics.mindsetBoards;
    },
    positiveFeedbackRules() {
      return this.analytics.positiveFeedbackRules;
    },
  },
  watch: {
    '$i18n.locale'() {
      this.fetchAnalytics();
    },
  },
  created() {
    this.fetchAnalytics();
  },
  methods: {
    async fetchAnalytics() {
      this.loading = true;
      this.errorMessage = '';
      try {
        const analytics = await loadGaAdminAnalytics();
        this.analytics = {
          ...this.analytics,
          ...analytics,
        };
      } catch (e) {
        console.error(e);
        this.errorMessage = this.$t('ga_admin_error_overview');
      } finally {
        this.loading = false;
      }
    },
    openAi(message) {
      this.$root.$emit('open-ai-chat', message, true, 'SRCopilotInsightGBA');
    },
  },
};
</script>

<style scoped lang="scss">
.ga-admin-page {
  min-height: 100%;
  background-color: #f2f5f9;
  background: linear-gradient(175deg, #e6f0ff 10%, #f0e9f4 30%, #f2f5f9 50%);
}

.page-body {
  padding: 12px 12px 90px;
}

.state-card {
  margin-bottom: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #fff;
  color: #666;
  font-size: 13px;
  line-height: 1.5;
}

.state-card.warning {
  color: #aa341b;
}

.loading-card {
  min-height: 240px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  border-radius: 16px;
  background: #fff;
  color: #666;
  font-size: 13px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.loading-spinner {
  width: 28px;
  height: 28px;
  border: 3px solid #f3d8d2;
  border-top-color: #aa341b;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.stats-grid,
.board-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.section-card,
.stat-card,
.board-card {
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.section-card {
  margin-bottom: 12px;
  padding: 15px;
}

.section-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.section-title {
  font-size: 17px;
  font-weight: 700;
  color: #333;
}

.section-desc {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.5;
  color: #999;
}

.ghost-btn {
  flex-shrink: 0;
  border: 1.5px solid #aa341b;
  border-radius: 999px;
  padding: 8px 12px;
  background: #fff;
  color: #aa341b;
  font-size: 12px;
}

.ai-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 0;
  background: linear-gradient(135deg, #4d50e9 0%, #b84fd7 48%, #aa341b 100%);
  color: #fff;
  box-shadow: 0 6px 14px rgba(170, 52, 27, 0.2);
}

.ai-btn-icon {
  font-size: 13px;
}

.stat-card {
  padding: 14px;
  background: linear-gradient(180deg, #fff, #f8f6f5);
}

.stat-title {
  font-size: 12px;
  color: #666;
}

.stat-value {
  margin-top: 8px;
  font-size: 28px;
  font-weight: 700;
  color: #333;
}

.stat-change {
  margin-top: 8px;
  font-size: 12px;
  font-weight: 600;
}

.stat-change.positive {
  color: #0e9f6e;
}

.stat-change.neutral {
  color: #1f3c88;
}

.stat-change.warning {
  color: #aa341b;
}

.stat-desc {
  margin-top: 8px;
  font-size: 12px;
  line-height: 1.5;
  color: #999;
}

.summary-list {
  display: grid;
  gap: 10px;
}

.summary-item {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 12px 14px;
  border-radius: 14px;
  background: #fff;
  border: 1px solid #f2f5f9;
  font-size: 13px;
  line-height: 1.6;
  color: #333;
}

.summary-dot {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  margin-top: 7px;
  border-radius: 50%;
  background: #aa341b;
}

.board-card {
  padding: 14px;
  background: #fff;
}

.hero-grid {
  margin-bottom: 12px;
}

.board-title {
  font-size: 15px;
  font-weight: 700;
  color: #333;
}

.board-subtitle {
  margin-top: 4px;
  font-size: 11px;
  color: #999;
}

.board-summary {
  margin-top: 10px;
  font-size: 12px;
  line-height: 1.6;
  color: #666;
}

.metric-row,
.tag-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.metric-chip {
  min-width: 88px;
  padding: 10px;
  border-radius: 12px;
  background: #f2f5f9;
}

.metric-label {
  display: block;
  font-size: 11px;
  color: #999;
}

.metric-value {
  display: block;
  margin-top: 6px;
  font-size: 16px;
  font-weight: 700;
  color: #333;
}

.soft-tag {
  padding: 6px 10px;
  border-radius: 999px;
  background: #fceceb;
  font-size: 11px;
  color: #aa341b;
}

.soft-tag.empty {
  background: #f8f6f5;
  color: #999;
}

.rule-note {
  margin-top: 12px;
  padding: 12px;
  border-radius: 12px;
  background: #f8f6f5;
}

.rule-row {
  font-size: 11px;
  line-height: 1.6;
  color: #7a4d42;
}

.rule-row + .rule-row {
  margin-top: 6px;
}

@media (max-width: 360px) {
  .stats-grid,
  .board-grid {
    grid-template-columns: 1fr;
  }
}
</style>
